/**
 * Global command palette — Cmd+K / Ctrl+K overlay search.
 *
 * Three result groups:
 *   1. Employees   — debounced server-side search via `useEmployees({search})`
 *   2. Pages       — hardcoded mirror of `NAV_GROUPS` from RootLayout
 *   3. Actions     — hardcoded common quick actions
 *
 * Empty query state shows up to 10 recent selections from localStorage
 * (`cmdk.recents`). Each selection navigates and (optionally) bumps the
 * recents list.
 *
 * Keyboard model (handled manually so we don't need the `cmdk` package):
 *   - ArrowUp/ArrowDown — move highlight across the flat result list
 *   - Enter             — activate highlighted row
 *   - Escape            — close (also handled by Radix Dialog)
 *
 * Closes on: Escape, outside click (Radix overlay), route change, selecting
 * a result.
 */
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  ArrowRight,
  Calendar,
  FileText,
  Loader2,
  Search,
  Sparkles,
  User,
  type LucideIcon,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Dialog, DialogOverlay, DialogPortal } from './ui/dialog';

import { useEmployees, type EmployeeRow } from '@/features/employees/employees.api';
import { cn } from '@/lib/utils';
import { useCommandPaletteStore } from '@/stores/command-palette.store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ResultType = 'employee' | 'page' | 'action';

interface BaseResult {
  type: ResultType;
  /** Stable key for keyboard nav + recents dedup. */
  id: string;
  label: string;
  /** Subtitle line — designation, action hint, parent group, etc. */
  hint?: string;
  /** Where to navigate on activation. */
  path: string;
  /** Lucide icon for non-employee rows. */
  icon?: LucideIcon;
  /** Optional avatar URL for employee rows. */
  avatarUrl?: string;
}

interface RecentSelection {
  type: ResultType;
  id: string;
  label: string;
  path: string;
}

const RECENTS_KEY = 'cmdk.recents';
const RECENTS_MAX = 10;
const SEARCH_DEBOUNCE_MS = 200;

// ---------------------------------------------------------------------------
// Static data — pages + actions
// ---------------------------------------------------------------------------

/**
 * Hardcoded mirror of `NAV_GROUPS` from `RootLayout.tsx`. Keep in sync when
 * routes are added; we deliberately avoid importing from RootLayout to keep
 * this component self-contained and tree-shake friendly.
 */
const PAGE_RESULTS: BaseResult[] = [
  {
    type: 'page',
    id: 'page:dashboard',
    label: 'Dashboard',
    hint: 'My Portal',
    path: '/dashboard',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:ess',
    label: 'My Portal',
    hint: 'My Portal',
    path: '/ess',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:employees',
    label: 'Employees',
    hint: 'Core HR',
    path: '/employees',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:org',
    label: 'Organisation',
    hint: 'Core HR',
    path: '/org',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:onboarding',
    label: 'Onboarding',
    hint: 'Core HR',
    path: '/onboarding',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:lifecycle',
    label: 'Lifecycle',
    hint: 'Core HR',
    path: '/lifecycle',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:exit',
    label: 'Exit Management',
    hint: 'Core HR',
    path: '/exit',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:attendance',
    label: 'Attendance',
    hint: 'Time & Pay',
    path: '/attendance',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:leave',
    label: 'Leave',
    hint: 'Time & Pay',
    path: '/leave',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:payroll',
    label: 'Payroll',
    hint: 'Time & Pay',
    path: '/payroll',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:expenses',
    label: 'Expenses',
    hint: 'Time & Pay',
    path: '/expenses',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:compensation',
    label: 'Compensation',
    hint: 'Time & Pay',
    path: '/compensation',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:recruitment',
    label: 'Recruitment',
    hint: 'Talent',
    path: '/recruitment',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:careers',
    label: 'Careers Page',
    hint: 'Talent',
    path: '/recruitment/careers-page',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:performance',
    label: 'Performance',
    hint: 'Talent',
    path: '/performance',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:training',
    label: 'Training',
    hint: 'Talent',
    path: '/training',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:engagement',
    label: 'Engagement',
    hint: 'Talent',
    path: '/engagement',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:assets',
    label: 'Assets',
    hint: 'Operations',
    path: '/assets',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:helpdesk',
    label: 'Helpdesk',
    hint: 'Operations',
    path: '/helpdesk',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:documents',
    label: 'Documents',
    hint: 'Operations',
    path: '/documents',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:vendors',
    label: 'Vendors',
    hint: 'Operations',
    path: '/vendors',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:compliance',
    label: 'Compliance',
    hint: 'Compliance',
    path: '/compliance',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:whistleblower',
    label: 'Whistleblower',
    hint: 'Compliance',
    path: '/whistleblower',
    icon: FileText,
  },
  { type: 'page', id: 'page:dei', label: 'DEI', hint: 'Compliance', path: '/dei', icon: FileText },
  {
    type: 'page',
    id: 'page:workforce',
    label: 'Workforce',
    hint: 'Compliance',
    path: '/workforce',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:reports',
    label: 'Reports',
    hint: 'Analytics',
    path: '/reports',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:ai',
    label: 'AI Copilot',
    hint: 'AI',
    path: '/ai-copilot',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:subscription',
    label: 'Subscription',
    hint: 'Admin',
    path: '/subscription',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:settings',
    label: 'Company Settings',
    hint: 'Admin',
    path: '/settings/company',
    icon: FileText,
  },
  {
    type: 'page',
    id: 'page:admin',
    label: 'Super Admin',
    hint: 'Platform',
    path: '/admin',
    icon: FileText,
  },
];

const ACTION_RESULTS: BaseResult[] = [
  {
    type: 'action',
    id: 'action:apply-leave',
    label: 'Apply leave',
    hint: 'Submit a new leave request',
    path: '/leave?action=apply',
    icon: Calendar,
  },
  {
    type: 'action',
    id: 'action:add-employee',
    label: 'Add employee',
    hint: 'Create a new employee record',
    path: '/employees?action=create',
    icon: User,
  },
  {
    type: 'action',
    id: 'action:generate-payslip',
    label: 'Generate payslip',
    hint: 'Run payroll for current period',
    path: '/payroll',
    icon: Sparkles,
  },
  {
    type: 'action',
    id: 'action:my-profile',
    label: 'View my profile',
    hint: 'Open Employee Self-Service',
    path: '/ess',
    icon: User,
  },
  {
    type: 'action',
    id: 'action:settings',
    label: 'Settings',
    hint: 'Company configuration',
    path: '/settings/company',
    icon: Sparkles,
  },
];

// ---------------------------------------------------------------------------
// Recents helpers
// ---------------------------------------------------------------------------

function readRecents(): RecentSelection[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is RecentSelection =>
        typeof r === 'object' &&
        r !== null &&
        typeof (r as RecentSelection).id === 'string' &&
        typeof (r as RecentSelection).label === 'string' &&
        typeof (r as RecentSelection).path === 'string',
    );
  } catch {
    return [];
  }
}

function pushRecent(sel: RecentSelection): void {
  try {
    const current = readRecents().filter((r) => r.id !== sel.id);
    const next = [sel, ...current].slice(0, RECENTS_MAX);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // localStorage unavailable (private mode, quota) — silently skip.
  }
}

// ---------------------------------------------------------------------------
// Debounce hook (local — no shared util in repo)
// ---------------------------------------------------------------------------

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

// ---------------------------------------------------------------------------
// Filtering — simple case-insensitive substring match on label + hint
// ---------------------------------------------------------------------------

function matches(query: string, ...fields: (string | undefined)[]): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return fields.some((f) => f?.toLowerCase().includes(q));
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CommandPalette(): ReactNode {
  const isOpen = useCommandPaletteStore((s) => s.isOpen);
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const close = useCommandPaletteStore((s) => s.close);
  const navigate = useNavigate();
  const location = useLocation();

  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const trimmed = debouncedQuery.trim();

  // Server-side employee search — only fires when query non-empty.
  const employeesQuery = useEmployees(trimmed.length > 0 ? { search: trimmed } : undefined);
  const employeeRows: EmployeeRow[] = useMemo(() => {
    if (!trimmed) return [];
    return (employeesQuery.data?.items ?? []).slice(0, 6);
  }, [employeesQuery.data?.items, trimmed]);

  // Recents shown only when query is empty.
  const [recents, setRecents] = useState<RecentSelection[]>([]);
  useEffect(() => {
    if (isOpen) setRecents(readRecents());
  }, [isOpen]);

  // Filter pages + actions client-side.
  const filteredPages = useMemo(
    () => PAGE_RESULTS.filter((p) => matches(query, p.label, p.hint)).slice(0, 8),
    [query],
  );
  const filteredActions = useMemo(
    () => ACTION_RESULTS.filter((a) => matches(query, a.label, a.hint)),
    [query],
  );

  // Build the flat ordered list driving keyboard navigation.
  interface Group {
    title: string;
    items: BaseResult[];
  }
  const groups: Group[] = useMemo(() => {
    if (!trimmed && recents.length > 0) {
      return [
        {
          title: 'Recent',
          items: recents.map<BaseResult>((r) => ({
            type: r.type,
            id: r.id,
            label: r.label,
            path: r.path,
            icon: r.type === 'employee' ? User : r.type === 'page' ? FileText : Sparkles,
          })),
        },
      ];
    }
    const employeeItems: BaseResult[] = employeeRows.map((e) => ({
      type: 'employee',
      id: `employee:${e.id}`,
      label: e.displayName,
      hint: e.jobTitle ?? e.workEmail,
      path: `/employees/${e.id}`,
    }));
    const out: Group[] = [];
    if (trimmed && employeeItems.length > 0) out.push({ title: 'Employees', items: employeeItems });
    if (filteredPages.length > 0) out.push({ title: 'Pages', items: filteredPages });
    if (filteredActions.length > 0) out.push({ title: 'Actions', items: filteredActions });
    return out;
  }, [trimmed, recents, employeeRows, filteredPages, filteredActions]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  // Reset highlight whenever the result set changes.
  useEffect(() => {
    setHighlight(0);
  }, [trimmed, flat.length]);

  // Reset state when palette closes; autofocus when it opens.
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setHighlight(0);
    }
  }, [isOpen]);

  // Close on route change.
  const lastPath = useRef(location.pathname + location.search);
  useEffect(() => {
    const current = location.pathname + location.search;
    if (current !== lastPath.current) {
      lastPath.current = current;
      if (isOpen) close();
    }
  }, [location.pathname, location.search, isOpen, close]);

  const handleSelect = useCallback(
    (item: BaseResult) => {
      pushRecent({ type: item.type, id: item.id, label: item.label, path: item.path });
      close();
      navigate(item.path);
    },
    [close, navigate],
  );

  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => (flat.length === 0 ? 0 : (h + 1) % flat.length));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => (flat.length === 0 ? 0 : (h - 1 + flat.length) % flat.length));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = flat[highlight];
        if (item) handleSelect(item);
      }
    },
    [flat, highlight, handleSelect],
  );

  // Scroll highlighted row into view as the user navigates.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-cmdk-index="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (open ? null : close())}>
      <DialogPortal>
        <DialogOverlay className="bg-black/40 backdrop-blur-sm" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => {
            // Default Radix focus jumps to the close button — we want the input.
            e.preventDefault();
            inputRef.current?.focus();
          }}
          className={cn(
            'fixed z-50 bg-white shadow-2xl outline-none',
            // Mobile: full screen
            'inset-0 flex flex-col',
            // Desktop: centered modal
            'sm:inset-auto sm:left-1/2 sm:top-[15%] sm:w-full sm:max-w-[600px] sm:-translate-x-1/2 sm:rounded-lg sm:border',
            // Animation
            'duration-150 data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
            'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
          )}
        >
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>

          {/* Search input */}
          <div className="flex items-center gap-3 border-b px-4">
            <Search size={18} className="text-muted-foreground shrink-0" aria-hidden />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search employees, pages, or actions…"
              className="h-12 w-full bg-transparent text-base outline-none placeholder:text-muted-foreground"
              autoFocus
              aria-label="Command palette search"
            />
            {employeesQuery.isFetching && trimmed.length > 0 && (
              <Loader2
                size={16}
                className="text-muted-foreground shrink-0 animate-spin"
                aria-hidden
              />
            )}
          </div>

          {/* Results */}
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto sm:max-h-[420px] py-2"
            role="listbox"
          >
            {flat.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                {trimmed ? 'No results.' : 'Type to search employees, pages, or actions'}
              </div>
            ) : (
              groups.map((group) => (
                <div key={group.title} className="mb-1">
                  <div className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {group.title}
                  </div>
                  <div>
                    {group.items.map((item) => {
                      const flatIdx = flat.indexOf(item);
                      const active = flatIdx === highlight;
                      return (
                        <ResultRow
                          key={item.id}
                          item={item}
                          active={active}
                          flatIdx={flatIdx}
                          onSelect={() => handleSelect(item)}
                          onMouseEnter={() => setHighlight(flatIdx)}
                        />
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="hidden items-center justify-between border-t bg-slate-50 px-4 py-2 text-[11px] text-muted-foreground sm:flex">
            <span>HRMS Command Palette</span>
            <span className="flex items-center gap-3">
              <span>↑↓ navigate</span>
              <span>↵ select</span>
              <span>esc close</span>
            </span>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface ResultRowProps {
  item: BaseResult;
  active: boolean;
  flatIdx: number;
  onSelect: () => void;
  onMouseEnter: () => void;
}

function ResultRow({ item, active, flatIdx, onSelect, onMouseEnter }: ResultRowProps): ReactNode {
  const Icon = item.icon;
  return (
    <button
      type="button"
      data-cmdk-index={flatIdx}
      onClick={onSelect}
      onMouseEnter={onMouseEnter}
      role="option"
      aria-selected={active}
      className={cn(
        'flex h-11 w-full items-center gap-3 px-4 text-left text-sm transition-colors',
        active ? 'bg-slate-100' : 'hover:bg-slate-50',
      )}
    >
      {item.type === 'employee' ? (
        <Avatar className="h-7 w-7">
          {item.avatarUrl && <AvatarImage src={item.avatarUrl} alt="" />}
          <AvatarFallback className="text-[10px] font-medium">
            {initialsOf(item.label)}
          </AvatarFallback>
        </Avatar>
      ) : (
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-100 text-slate-600">
          {Icon ? <Icon size={15} aria-hidden /> : <FileText size={15} aria-hidden />}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-slate-900">{item.label}</div>
        {item.hint && <div className="truncate text-xs text-muted-foreground">{item.hint}</div>}
      </div>
      {active && <ArrowRight size={14} className="text-muted-foreground shrink-0" aria-hidden />}
    </button>
  );
}
